import { SpanStatusCode } from '@opentelemetry/api';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { updateDeviceStatus } from '../common/device/initialize-devices';
import { DeviceType } from '../common/enumerations/device-type';
import XLRLogger from '../common/helpers/logger';
import { IReceipt } from '../common/interfaces/dtos/IReceipt';
import { store } from '../common/redux/configure-store';
import { EmeraldError, EmeraldErrorSeverity } from 'src/common/api/emerald-error';
import {
  PrintCheckEndorsementFailure,
  PrintCheckEndorsementRemoveTimeOut,
  PrintReceiptFailure,
} from 'src/common/helpers/error-code';
import { RdsResponseType } from './device-enums';
import { DeviceResponse, rdsRequest } from './rds-connection';
import { showDeviceAlertDialog } from 'src/device-status/device-status';
import { popupMessage } from 'src/messages/business-logic/popup-message';

export const printReceipts = async (
  receipts: IReceipt[],
  hidePrinterError: boolean = false,
  handleInternalError?: () => void,
): Promise<void> => {
  return new Promise(resolve => {
    const span = createSpan('print-receipts');

    const logError = () => {
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: 'printReceipts error - No connection to printer',
      });
      XLRLogger.error({
        message: 'No connection to printer',
        source: 'devices/printer-operations.ts',
      });
    };

    const printer = store
      .getState()
      .deviceStatus.devices.find(d => d.deviceType === DeviceType.Printer);

    if (printer?.initialized) {
      receipts.forEach(receipt => printReceipt(receipt));
      resolve();
    } else if (!printer || printer?.hidePrinterError) {
      logError();
      resolve();
    } else {
      handleInternalError && handleInternalError();
      const printerErrorCallback = (hidePrinterError: boolean) => {
        printReceipts(receipts, hidePrinterError)
          .catch(err => {
            popupMessage(err);
          })
          .finally(resolve);
      };

      logError();

      hidePrinterError ? resolve() : showDeviceAlertDialog(printerErrorCallback);
    }
    endSpan(span);
  });
};

const printReceipt = async (data: IReceipt): Promise<boolean> => {
  return rdsRequest({
    url: '/printer/print',
    method: 'POST',
    data,
  }).then((value): boolean => {
    if (value !== null) {
      const response: DeviceResponse = value as DeviceResponse;
      if (response.responseType === RdsResponseType.ERROR) {
        XLRLogger.debug({
          message: 'RDS Printer Error -- non-null response from print call',
          source: 'devices/printer-operations.ts',
          data: response,
        });
        popupMessage(new EmeraldError(PrintReceiptFailure, '', EmeraldErrorSeverity.Error, data));
        return false;
      }
      if (response.responseType === RdsResponseType.PRINT) {
        XLRLogger.debug({
          message: 'RDS Printer -- success response from print call',
          source: 'devices/printer-operations.ts',
          data: response,
        });
        return true;
      }
    }
    XLRLogger.debug({
      message: 'RDS Printer -- unexpected response from print call',
      source: 'devices/printer-operations.ts',
      data: { value },
    });
    return true;
  });
};

export const endorseCheck = async (data: IReceipt): Promise<void> => {
  return rdsRequest({
    url: '/printer/print-slip',
    method: 'POST',
    data,
  }).then((value): void => {
    if (value !== null) {
      const response: DeviceResponse = value as DeviceResponse;
      if (response.responseType === RdsResponseType.ERROR) {
        XLRLogger.debug({
          message: 'RDS Printer Error -- non-null response from print call',
          source: 'devices/printer-operations.ts',
          data: response,
        });
        throw new EmeraldError(PrintCheckEndorsementFailure, '', EmeraldErrorSeverity.Error, data);
      }
      if (response.responseType === RdsResponseType.PRINT_SLIP_REMOVE_TIME_OUT) {
        XLRLogger.debug({
          message: 'RDS Printer Remove Time out -- non-null response from print call',
          source: 'devices/printer-operations.ts',
          data: response,
        });
        throw new EmeraldError(
          PrintCheckEndorsementRemoveTimeOut,
          '',
          EmeraldErrorSeverity.Error,
          data,
        );
      }
      if (response.responseType === RdsResponseType.PRINT_SLIP) {
        XLRLogger.debug({
          message: 'RDS Printer -- success response from print call',
          source: 'devices/printer-operations.ts',
          data: response,
        });
      }
    }
    XLRLogger.debug({
      message: 'RDS Printer -- unexpected response from print call',
      source: 'devices/printer-operations.ts',
      data: { value },
    });
  });
};

export const handlePrinterMessage = (printerData: DeviceResponse): void => {
  const deviceName = printerData.metaDataMap.logicalName;
  if (printerData.error) {
    XLRLogger.error({
      message: 'RDS Printer Error',
      source: 'devices/printer-operations.ts',
      data: printerData,
    });
    return;
  }

  if (printerData.responseType === RdsResponseType.INITIALIZATION) {
    XLRLogger.debug({
      message: `Printer Initialization: ${printerData.message}`,
      source: 'devices/printer-operations.ts',
      data: printerData,
    });
    return;
  }

  if (printerData.responseType === RdsResponseType.CONNECTED) {
    XLRLogger.debug({
      message: `Printer Connected: ${printerData.message}`,
      source: 'devices/printer-operations.ts',
      data: printerData,
    });
    return;
  }

  if (printerData.responseType === RdsResponseType.OUTPUT_COMPLETE) {
    XLRLogger.debug({
      message: `Printer Output Complete: ${printerData.message}`,
      source: 'devices/printer-operations.ts',
      data: printerData,
    });
    return;
  }

  if (printerData.responseType === RdsResponseType.STATUS_UPDATE) {
    XLRLogger.debug({
      message: `Printer Status Update: ${printerData.message}`,
      source: 'devices/printer-operations.ts',
      data: printerData,
    });
    return;
  }

  if (printerData.responseType === RdsResponseType.HEALTH_CHECK_SUCCESS) {
    if (deviceName) {
      updateDeviceStatus({
        deviceName,
        initialized: true,
        deviceType: DeviceType.Printer,
      });
    }
    XLRLogger.debug({
      message: `Printer Status: ${printerData.message}`,
      source: 'devices/printer-operations.ts',
      data: printerData,
    });
    return;
  }

  if (printerData.responseType === RdsResponseType.HEALTH_CHECK_FAILURE) {
    if (deviceName) {
      updateDeviceStatus({
        deviceName,
        initialized: false,
        deviceType: DeviceType.Printer,
      });
    }
    XLRLogger.debug({
      message: `Printer Status: ${printerData.message}`,
      source: 'devices/printer-operations.ts',
      data: printerData,
    });
    return;
  }

  XLRLogger.info({
    message: 'Unrecognized message from Printer',
    source: 'devices/printer-operations.ts',
    data: printerData,
  });
};
